/**
 * CookieBanner — GDPR Compliant Opt-In Cookie Banner
 * Minimal, non-invasivo — slim bottom bar senza backdrop
 * Per visitatori profilo pubblico. I clienti/attività accettano in onboarding.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent, type CookieConsent } from '../../contexts/CookieContext';
import { Cookie, ChevronUp, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

export function CookieBanner() {
    const { t } = useTranslation();
    const { consent, isOpen, acceptAll, rejectAll, savePreferences } = useCookieConsent();
    const [showCustomize, setShowCustomize] = useState(false);
    const [localPrefs, setLocalPrefs] = useState<CookieConsent>({
        essential: true,
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        if (isOpen) {
            setLocalPrefs({
                essential: true,
                analytics: consent.analytics,
                marketing: consent.marketing
            });
        }
    }, [isOpen, consent]);

    if (!isOpen) return null;

    return (
        <>
            {/* Slim bottom bar — no backdrop, non-invasive */}
            <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slideUp">
                <div className="bg-white/95 backdrop-blur-xl border-t border-slate-200/80 shadow-lg shadow-slate-900/5">

                    {/* Main compact bar */}
                    <div className="max-w-6xl mx-auto px-4 py-3 sm:py-2.5 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 pb-[calc(env(safe-area-inset-bottom)+12px)] sm:pb-3">
                        <div className="flex items-start gap-3 w-full sm:w-auto sm:flex-1">
                            <Cookie className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5 hidden sm:block" />

                            <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed flex-1">
                                {t('legal.cookie_banner.description', 'Utilizziamo cookie per migliorare la tua esperienza.')}
                                {' '}
                                <Link to="/privacy-policy" className="text-sky-500 hover:underline font-medium">
                                    Privacy Policy
                                </Link>
                            </p>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto border-t sm:border-t-0 border-slate-100 pt-0 sm:pt-0">
                            <button
                                onClick={() => setShowCustomize(!showCustomize)}
                                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
                                title={t('legal.cookie_banner.customize', 'Personalizza')}
                            >
                                <Settings className="w-4 h-4" />
                            </button>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={rejectAll}
                                    className="text-[10px] sm:text-xs text-slate-500 hover:text-slate-700 font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-all whitespace-nowrap"
                                >
                                    {t('legal.cookie_banner.reject_all', 'Solo Essenziali')}
                                </button>
                                <button
                                    onClick={acceptAll}
                                    className="text-[10px] sm:text-xs text-white font-bold px-4 py-1.5 rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 shadow-sm transition-all whitespace-nowrap"
                                >
                                    {t('legal.cookie_banner.accept_all', 'Accetta')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Expandable customize panel */}
                    {showCustomize && (
                        <div className="border-t border-slate-100 bg-slate-50/80 animate-fadeIn">
                            <div className="max-w-6xl mx-auto px-4 py-4">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {/* Essential */}
                                    <MiniToggle
                                        label={t('legal.cookie_banner.essential_label', 'Essenziali')}
                                        desc={t('legal.cookie_banner.essential_desc', 'Necessari per il funzionamento.')}
                                        checked={true}
                                        disabled={true}
                                        onChange={() => { }}
                                    />
                                    {/* Analytics */}
                                    <MiniToggle
                                        label={t('legal.cookie_banner.analytics_label', 'Analisi')}
                                        desc={t('legal.cookie_banner.analytics_desc', 'Google Analytics, Clarity.')}
                                        checked={localPrefs.analytics}
                                        onChange={(v) => setLocalPrefs(p => ({ ...p, analytics: v }))}
                                    />
                                    {/* Marketing */}
                                    <MiniToggle
                                        label={t('legal.cookie_banner.marketing_label', 'Marketing')}
                                        desc={t('legal.cookie_banner.marketing_desc', 'Meta Pixel, Google Ads.')}
                                        checked={localPrefs.marketing}
                                        onChange={(v) => setLocalPrefs(p => ({ ...p, marketing: v }))}
                                    />
                                </div>
                                <div className="flex items-center justify-end gap-2 mt-3">
                                    <button
                                        onClick={() => { savePreferences(localPrefs); setShowCustomize(false); }}
                                        className="text-xs text-white font-bold px-4 py-1.5 rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 shadow-sm transition-all"
                                    >
                                        {t('legal.cookie_banner.save_preferences', 'Salva Preferenze')}
                                    </button>
                                    <button
                                        onClick={() => setShowCustomize(false)}
                                        className="text-xs text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
                                    >
                                        <ChevronUp className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slideUp {
                    animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>
        </>
    );
}

/** Compact toggle per categoria cookie */
function MiniToggle({ label, desc, checked, disabled, onChange }: {
    label: string;
    desc: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (value: boolean) => void;
}) {
    return (
        <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${checked ? 'bg-white border-sky-100' : 'bg-white/60 border-slate-100'
            }`}>
            <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-700 text-xs">{label}</div>
                <div className="text-[10px] text-slate-400 leading-tight mt-0.5 truncate">{desc}</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked)}
                    className="sr-only peer"
                />
                <div className={`w-9 h-5 rounded-full transition-all ${disabled
                    ? 'bg-sky-400 cursor-not-allowed'
                    : checked
                        ? 'bg-gradient-to-r from-sky-400 to-indigo-500'
                        : 'bg-slate-200'
                    } after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all after:shadow-sm ${checked ? 'after:translate-x-4' : ''
                    }`} />
            </label>
        </div>
    );
}

export default CookieBanner;
